import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { CircleChevronDown } from 'lucide-react';
import type { TenantDetail } from '../../lib/queries/customers';

function formatChartDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

function periodLabel(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) =>
    `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
  return `${fmt(start)} – ${fmt(end)}`;
}

interface OverviewProps {
  isLoading: boolean;
  tenant?: TenantDetail;
  pct: number;
  remaining: number;
}

export default function Overview({
  isLoading,
  tenant,
  pct,
  remaining,
}: OverviewProps) {
  const [tokenUsageOpen, setTokenUsageOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* Info row */}
      {isLoading ? (
        <div className="h-12 animate-pulse rounded bg-white/6" />
      ) : tenant ? (
        <div className="bg-bg-elevated grid grid-cols-1 gap-0 rounded-lg lg:grid-cols-6 lg:gap-6 lg:bg-transparent">
          {[
            { label: 'Plan', value: tenant.planName },
            {
              label: 'Contract start',
              value: formatDate(tenant.createdAt),
            },
            { label: 'Renewal', value: '—' },
            { label: 'Primary contact', value: '—' },
            { label: 'Contact email', value: '—' },
            { label: 'Onboarded by', value: '—' },
          ].map(({ label, value }, index) => (
            <div
              key={label}
              className={[
                'flex flex-row items-center justify-between px-6 py-3 lg:flex-col lg:items-start lg:px-0 lg:py-2',
                index < 5 ? 'border-b-primary/8 border-b lg:border-none' : '',
              ].join(' ')}
            >
              <p className="text-muted text-[0.625rem]">{label}</p>
              <p className="text-primary mt-1 text-sm">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      {/* Token usage card */}
      <div className="bg-bg-elevated rounded-lg border border-white/6 p-5">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-subtle text-[0.625rem] font-semibold tracking-widest uppercase md:text-xs">
            <span>Token usage</span>{' '}
            <span className="hidden md:inline-block">· Current period</span>
          </span>
          <span className="text-muted hidden text-xs md:inline-block">
            {periodLabel()}
          </span>
        </div>

        {isLoading ? (
          <div className="h-40 animate-pulse rounded bg-white/6" />
        ) : tenant ? (
          <>
            <div className="mb-4 flex items-end gap-4">
              <span className="text-subtle text-4xl font-bold tabular-nums">
                {formatTokens(tenant.periodUsage)}
              </span>
              <div className="mb-1 flex-1">
                <div className="mb-1 flex items-center justify-between">
                  <div className="mr-4 h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-green-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-right">
                    <span className="text-accent text-sm font-semibold">
                      {pct}%
                    </span>
                    <p className="text-muted text-xs">
                      {formatTokens(Math.max(0, remaining))} remaining
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* mobile layout */}
            <div>
              <div className="flex justify-between md:hidden">
                <p className="text-primary text-xs">View details</p>
                <button
                  className="text-primary"
                  onClick={() => setTokenUsageOpen((prev) => !prev)}
                >
                  <CircleChevronDown
                    className={[
                      'text-primary transition-transform duration-200',
                      tokenUsageOpen ? 'rotate-180' : '',
                    ].join(' ')}
                    strokeWidth={1.5}
                    size={22}
                  />
                </button>
              </div>
              {tokenUsageOpen && (
                <ResponsiveContainer width="100%" height={140}>
                  <AreaChart
                    data={tenant.dailyUsage}
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="usageGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#5e6ad2"
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor="#5e6ad2"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatChartDate}
                      tick={{ fill: '#8e8ea8', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        background: '#141418',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                      labelFormatter={formatChartDate}
                      formatter={(v: number) => [formatTokens(v), 'Tokens']}
                    />
                    <Area
                      type="monotone"
                      dataKey="tokens"
                      stroke="#5e6ad2"
                      strokeWidth={2}
                      fill="url(#usageGradient)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="hidden md:block">
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart
                  data={tenant.dailyUsage}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="usageGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#5e6ad2" stopOpacity={0.4} />
                      <stop
                        offset="95%"
                        stopColor="#5e6ad2"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatChartDate}
                    tick={{ fill: '#8e8ea8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{
                      background: '#141418',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    labelFormatter={formatChartDate}
                    formatter={(v: number) => [formatTokens(v), 'Tokens']}
                  />
                  <Area
                    type="monotone"
                    dataKey="tokens"
                    stroke="#5e6ad2"
                    strokeWidth={2}
                    fill="url(#usageGradient)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
